<template>
    <v-app>
        <v-layout v-if="route.name === 'NotFound'">
            <router-view></router-view>
        </v-layout>
        <v-layout v-else>
            <AppTopBar v-if="selectIsAuthenticated"></AppTopBar>
            <v-main>
                <AppSearchResult></AppSearchResult>
                <router-view></router-view>
                <ChatWidget v-if="selectIsAuthenticated" />
                <AppFooter v-if="selectIsAuthenticated && isDevelopment()" />
            </v-main>
            <div name="snackbars">
                <v-snackbar
                    v-model="showNotification"
                    :color="selectNotification?.length ? (get(first(selectNotification), 'type') ?? 'info') : ''"
                    :class="selectNotification?.length ? (get(first(selectNotification), 'type') ?? 'info') : ''"
                    :timeout="3000"
                    :top="'top'"
                    location="center center"
                    variant="tonal"
                >
                    <div class="snackbar-title">
                        <div class="title-wrap">
                            <v-icon
                                v-if="
                                    (get(first(selectNotification), 'type') ?? 'info') === 'error' ||
                                        (get(first(selectNotification), 'type') ?? 'info') === 'info' ||
                                        (get(first(selectNotification), 'type') ?? 'info') === 'warning'
                                "
                                icon="info"
                                size="small"
                            ></v-icon>
                            <v-icon
                                v-if="(get(first(selectNotification), 'type') ?? 'info') === 'success'"
                                icon="check_circle_outline"
                                size="small"
                            ></v-icon>
                            <span class="text-subtitle-1">
                                {{
                                    get(first(selectNotification), 'title') ??
                                        getNotificationDeafultTitleMessage(
                                            first(selectNotification) as INotificationMessage
                                        ).title
                                }}
                            </span>
                        </div>
                        <div class="title-actions">
                            <v-icon icon="close" size="small" @click="showNotification = false"></v-icon>
                        </div>
                    </div>

                    <p>
                        {{
                            get(first(selectNotification), 'message') ??
                                getNotificationDeafultTitleMessage(first(selectNotification) as INotificationMessage)
                                    .message
                        }}
                    </p>
                </v-snackbar>
            </div>
        </v-layout>
    </v-app>
</template>

<script lang="ts" setup>
import AppTopBar from './modules/layout/components/AppTopBar.vue';
import AppFooter from './modules/layout/components/AppFooter.vue';
import useAuthStore from './modules/auth/store/auth.store';
import { storeToRefs } from 'pinia';
import useUIStore, { type INotificationMessage } from './modules/layout/store/ui.store';
import { onMounted, provide, ref, watch } from 'vue';
import { delay, first, get, size } from 'lodash';
import AppSearchResult from './modules/layout/components/AppSearchResult.vue';
import { useI18n } from 'vue-i18n';
import { AuthService } from './modules/auth/services/auth.service';
import { useRoute } from 'vue-router';
import ChatWidget from './shared/components/ChatWidget.vue';

const { t } = useI18n();
const uiStore = useUIStore();
const authStore = useAuthStore();
const route = useRoute();
const authServices = new AuthService(import.meta.env);
const { selectNotification } = storeToRefs(uiStore);
const { selectIsAuthenticated } = storeToRefs(authStore);
const showNotification = ref(false);

provide('uiStore', uiStore);

watch(
    selectNotification,
    (value: INotificationMessage[]) => {
        showNotification.value = !!size(value);
    },
    { deep: true, immediate: true }
);

watch(
    showNotification,
    (value: boolean) => {
        if (value === false) {
            delay(() => uiStore.resetNotification(), 200);
        }
    },
    { deep: true }
);

const getNotificationDeafultTitleMessage = (
    notification: INotificationMessage
): {
    title: string;
    message: string;
} => {
    switch (notification?.type || 'error') {
        case 'success':
            return {
                title: t('notification_success_title'),
                message: t('notification_success_message'),
            };
        case 'info':
            return {
                title: t('notification_info_title'),
                message: t('notification_info_message'),
            };
        case 'warning':
            return {
                title: t('notification_warning_title'),
                message: t('notification_warning_message'),
            };
        case 'error':
            return {
                title: t('notification_error_title'),
                message: t('notification_error_message'),
            };
        default:
            return {
                title: '',
                message: '',
            };
    }
};

onMounted(() => {
    init();
});

const isDevelopment = (): boolean => {
    if (
        (import.meta.env.VITE_APP_MODE === 'development' && import.meta.env.VITE_APP_DEPLOYMENT_MODE === 'local') ||
        (import.meta.env.VITE_APP_MODE === 'development' && import.meta.env.VITE_APP_DEPLOYMENT_MODE === 'development')
    ) {
        return true;
    }
    return false;
};

async function init(): Promise<void> {
    await authServices.initMSAL();
    // router.push({
    //     name: 'Home',
    // });
}
</script>

<style lang="scss">
@forward '@/scss/main.scss';
@use './scss/variables' as v;

.v-main {
    background-color: #f5f5f5;
}

.v-overlay__content {
    &.v-snackbar__wrapper {
        background-color: #ffffff !important;
    }
}

.v-snackbar {
    &.error {
        .v-snackbar__content {
            background-color: v.$danger-100 !important;
            color: v.$danger-800 !important;
        }
    }

    &.success {
        .v-snackbar__content {
            background-color: v.$success-100 !important;
            color: v.$success-700 !important;
        }
    }
}

.snackbar-title {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;

    .title-wrap {
        span {
            padding-left: 8px;
        }

        .v-icon {
            position: relative;
            top: -2px;
        }
    }

    .title-actions {
        .v-icon {
            color: v.$error-color;
        }
    }
}
</style>
